// frontend/src/services/remediationOperationsApi.js

function buildQuery(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export async function fetchRemediationOperationsAnalytics({
  getJsonWithToken,
  token,
  framework,
  includeClosed = true,
  signal,
} = {}) {
  if (typeof getJsonWithToken !== "function") {
    throw new Error("getJsonWithToken is required to load remediation analytics.");
  }

  return getJsonWithToken(
    token,
    "/remediation/analytics",
    {
      framework,
      include_closed: includeClosed,
    },
    signal
  );
}

export async function fetchRemediationOperationsQueue({
  getJsonWithToken,
  token,
  framework,
  queue,
  status,
  validationStatus,
  ownerUserId,
  ownerName,
  priority,
  assignmentState,
  overdue,
  slaState,
  search,
  includeClosed = false,
  limit = 25,
  offset = 0,
  signal,
} = {}) {
  if (typeof getJsonWithToken !== "function") {
    throw new Error("getJsonWithToken is required to load remediation queue.");
  }

  return getJsonWithToken(
    token,
    "/remediation/queue",
    {
      framework,
      queue,
      status,
      validation_status: validationStatus,
      owner_user_id: ownerUserId,
      owner_name: ownerName,
      priority,
      assignment_state: assignmentState,
      overdue,
      sla_state: slaState,
      search,
      include_closed: includeClosed,
      limit,
      offset,
    },
    signal
  );
}

export async function fetchRemediationTaskClosureGuard({
  getJsonWithToken,
  token,
  taskId,
  signal,
} = {}) {
  if (typeof getJsonWithToken !== "function") {
    throw new Error("getJsonWithToken is required to load remediation closure guard.");
  }

  const cleanedTaskId = String(taskId || "").trim();
  if (!cleanedTaskId) {
    throw new Error("taskId is required to load remediation closure guard.");
  }

  return getJsonWithToken(
    token,
    `/remediation/tasks/${cleanedTaskId}/closure-guard`,
    {},
    signal
  );
}

export async function rerunRemediationTaskValidation({
  postJsonWithToken,
  token,
  taskId,
  signal,
} = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to re-run task validation.");
  }

  const cleanedTaskId = String(taskId || "").trim();
  if (!cleanedTaskId) {
    throw new Error("taskId is required to re-run task validation.");
  }

  return postJsonWithToken(
    token,
    `/validation/tasks/${cleanedTaskId}/rerun`,
    {},
    signal
  );
}

export async function validateReadyRemediationTasks({
  postJsonWithToken,
  token,
  framework,
  lastAssessmentJobId,
  limit = 100,
  signal,
} = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to run remediation validation.");
  }

  const cleanedFramework = String(framework || "").trim();
  if (!cleanedFramework) {
    throw new Error("framework is required to run remediation validation.");
  }

  return postJsonWithToken(
    token,
    "/remediation/tasks/validate-ready",
    {
      framework: cleanedFramework,
      last_assessment_job_id: lastAssessmentJobId || null,
      limit,
    },
    signal
  );
}

export async function repairRemediationValidationLinks({
  postJsonWithToken,
  token,
  taskId,
  validationResultId,
  framework,
  controlId,
  ruleId,
  resourceId,
  includeClosed = false,
  limit = 100,
  signal,
} = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to repair remediation validation links.");
  }

  return postJsonWithToken(
    token,
    `/validation/remediation-links/repair${buildQuery({
      task_id: taskId,
      validation_result_id: validationResultId,
      framework,
      control_id: controlId,
      rule_id: ruleId,
      resource_id: resourceId,
      include_closed: includeClosed,
      limit,
    })}`,
    {},
    signal
  );
}

export async function bulkCreateRemediationTasks({ postJsonWithToken, token, payload, signal } = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to bulk-create remediation tasks.");
  }

  return postJsonWithToken(token, "/remediation/tasks/bulk-create", payload || {}, signal);
}

export async function bulkAssignRemediationTasks({ postJsonWithToken, token, payload, signal } = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to bulk-assign remediation tasks.");
  }

  return postJsonWithToken(token, "/remediation/tasks/bulk-assign", payload || {}, signal);
}

export async function bulkUpdateRemediationStatus({ postJsonWithToken, token, payload, signal } = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to bulk-update remediation status.");
  }

  return postJsonWithToken(token, "/remediation/tasks/bulk-status", payload || {}, signal);
}

export async function recoverRemediationTaskWorkflow({
  postJsonWithToken,
  token,
  taskId,
  payload,
  signal,
} = {}) {
  if (typeof postJsonWithToken !== "function") {
    throw new Error("postJsonWithToken is required to recover remediation workflow.");
  }

  const cleanedTaskId = String(taskId || "").trim();

  if (!cleanedTaskId) {
    throw new Error("taskId is required to recover remediation workflow.");
  }

  return postJsonWithToken(
    token,
    `/remediation/tasks/${cleanedTaskId}/workflow/recover`,
    payload || {},
    signal
  );
}

export function buildRemediationOperationsQueuePath(params = {}) {
  return `/remediation/queue${buildQuery(params)}`;
}

export function buildRemediationOperationsAnalyticsPath(params = {}) {
  return `/remediation/analytics${buildQuery(params)}`;
}

export function buildRemediationTaskClosureGuardPath(taskId) {
  const cleanedTaskId = String(taskId || "").trim();
  return cleanedTaskId ? `/remediation/tasks/${cleanedTaskId}/closure-guard` : "";
}
